// Direct-to-password login links land the patient on their own login screen
// carrying a hint token in the URL fragment. The token identifies the account
// so the patient is not asked which phone or email they signed up with; the
// password still authenticates. It is not consumed on login and stays valid to
// its own 14-day expiry, so an older text still works:
//
//   https://<slug>.myclinically.com/secure/login?locale=es#lt=<token>
//
// The fragment is never sent to a server, but it is readable by anything in the
// page that touches location.href — Sentry stamps the URL onto every event and
// navigation breadcrumb. So the token is lifted into module state and the
// fragment blanked before any of that can run: captureLoginHintToken() is the
// first statement in src/main.tsx, ahead of Sentry.init and the router.
//
// The token is never logged, never persisted (no localStorage/sessionStorage),
// and never moved into a query param, where it would reach CloudFront and the
// Referer header.

const HINT_PARAM = 'lt';

let hintToken: string | null = null;
let captured = false;

/**
 * Extracts the hint token from a raw location hash. Tolerates an absent, empty,
 * or malformed fragment and one carrying unrelated keys; URLSearchParams also
 * percent-decodes the value, so an encoded token round-trips.
 */
export const parseLoginHintToken = (hash: string): string | null => {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!body) return null;

  const value = new URLSearchParams(body).get(HINT_PARAM);
  return value ? value.trim() || null : null;
};

/**
 * Reads the hint token once per page load and strips the fragment from the URL.
 * Idempotent, so a later caller gets the cached value rather than an empty hash.
 */
export const captureLoginHintToken = (): string | null => {
  if (captured) return hintToken;
  captured = true;

  if (typeof window === 'undefined') return null;

  hintToken = parseLoginHintToken(window.location.hash);
  if (!hintToken) return null;

  try {
    // pathname + search are rewritten verbatim: ?locale= has to survive.
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    // A context that refuses replaceState must not block startup — the token is
    // already in module state, which is all the app reads.
  }

  return hintToken;
};

/**
 * The hint token for this page load, or null. Token presence is the only signal
 * the portal has for the direct-to-password flow; it receives no feature flags.
 */
export const getLoginHintToken = (): string | null =>
  captured ? hintToken : captureLoginHintToken();

export const hasLoginHintToken = (): boolean => getLoginHintToken() !== null;

/**
 * Drops the token at the session boundary — called once the patient reaches the
 * authenticated portal (src/routes/secure/portal/route.tsx). Module state
 * outlives a logout, which is an SPA navigate rather than a reload, so without
 * this a shared device carries one patient's token into the next patient's
 * login screen.
 */
export const clearLoginHintToken = (): void => {
  hintToken = null;
  captured = true;
};
